import { useState, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTcGateState } from "@/zustand/TcGateState";
import { useAuthState } from "@/zustand/AuthState";
import { fetchCurrentTerms, acceptTerms } from "@/server/services/termsService";
import { commitPendingAuth, logoutUser } from "@/middleware/auth";
import {
  loadPendingAuth,
  clearPendingAuth,
  PendingAuthData,
} from "@/lib/pendingAuth";
import { QUERY_KEYS } from "@/config/queryKeys";
import { useToast } from "@/hooks/useToast";
import useAuthSession from "@/middleware/hooks/useAuthSession";

const preventDismiss = (event: Event) => {
  event.preventDefault();
};

const TcGate = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuthSession();
  const required = useTcGateState((s) => s.required);
  const pendingVersion = useTcGateState((s) => s.pendingVersion);
  const clearTcGate = useTcGateState((s) => s.clearTcGate);
  const setAcceptedVersion = useTcGateState((s) => s.setAcceptedVersion);
  const [pending, setPending] = useState<PendingAuthData | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);

  // Login-pending mode: sync the pending bundle from sessionStorage.
  // Skip when the user is already fully logged in (version-check mode).
  // If the flag survived a tab close but sessionStorage is gone, auto-clear
  // so the user is returned to the login page.
  useEffect(() => {
    if (!required) {
      return;
    }
    if (isLoggedIn) {
      return;
    }
    const bundle = loadPendingAuth();
    if (bundle) {
      setPending(bundle);
    } else {
      clearTcGate();
      clearPendingAuth();
      useAuthState.getState().resetAuthState();
    }
  }, [required, isLoggedIn, clearTcGate]);

  const {
    data: terms,
    isLoading,
    isError,
  } = useQuery({
    queryKey: QUERY_KEYS.termsCurrent,
    queryFn: fetchCurrentTerms,
    enabled: required && (isLoggedIn || Boolean(pending)),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (!required) {
    return null;
  }
  // Login-pending mode: wait until the bundle is loaded from sessionStorage
  if (!isLoggedIn && !pending) {
    return null;
  }

  const handleAccept = async () => {
    if (!pendingVersion) {
      return;
    }
    setIsAccepting(true);
    try {
      await acceptTerms(pendingVersion);
      setAcceptedVersion(pendingVersion);
      if (pending) {
        // Login-pending mode: commit the deferred auth bundle then navigate in
        await commitPendingAuth(pending);
        setIsAccepting(false);
        clearTcGate();
        clearPendingAuth();
        navigate("/", { replace: true });
      } else {
        // Version-check mode: user is already authenticated
        setIsAccepting(false);
        clearTcGate();
      }
    } catch {
      toast({
        variant: "destructive",
        title: t("tcGate.errorToastTitle"),
        description: t("tcGate.errorToastDescription"),
      });
      setIsAccepting(false);
    }
  };

  const handleCancel = () => {
    logoutUser();
  };

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[9998] bg-background" />
        <DialogPrimitive.Content
          onEscapeKeyDown={preventDismiss}
          onPointerDownOutside={preventDismiss}
          onInteractOutside={preventDismiss}
          className="fixed inset-0 z-[9999] flex flex-col bg-background pb-[env(safe-area-inset-bottom,0px)] pt-[env(safe-area-inset-top,0px)] focus:outline-none"
        >
          <div className="shrink-0 border-b border-border px-6 py-5 text-center">
            <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
              {t("terms.title")}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-0.5 text-xs text-muted-foreground">
              {t("terms.issuer")}
              <br />
              {terms && (
                <>
                  <br />
                  {t("terms.effectiveDate", {
                    date: new Date(terms.effective_date).toLocaleDateString(),
                  })}
                  <br />
                  {t("terms.version", { version: terms.version })}
                </>
              )}
            </DialogPrimitive.Description>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {isLoading && (
              <p className="py-20 text-center text-sm text-muted-foreground">
                {t("tcGate.loading")}
              </p>
            )}
            {isError && (
              <p className="py-20 text-center text-sm text-destructive">
                {t("tcGate.error")}
              </p>
            )}
            {terms && (
              <div className="prose prose-sm mx-auto max-w-3xl dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {terms.content}
                </ReactMarkdown>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-border bg-background px-6 py-4">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
              <button
                onClick={handleCancel}
                className="rounded-lg border border-border px-6 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                {t("tcGate.cancelButton")}
              </button>
              <button
                onClick={handleAccept}
                disabled={!terms || isAccepting}
                className="rounded-lg bg-primary px-8 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isAccepting ? t("tcGate.accepting") : t("tcGate.acceptButton")}
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default TcGate;
